import React, { useState, useEffect } from 'react';
import Helmet from '../components/Helmet/Helmet';
import CommonSection from '../components/Ui/CommonSection';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import useAuth from '../custom-hooks/useAuth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, db } from '../firebase.config';
import { getUserOrders } from '../services/orderService';
import '../styles/profile.css';

const Profile = () => {
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState('personal'); // 'personal' | 'address' | 'security'
  const [loading, setLoading] = useState(false);
  const [userOrders, setUserOrders] = useState([]);

  // Personal Info Form State
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [photoURL, setPhotoURL] = useState('');

  // Address State
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('Egypt');

  // Security Form State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [securityLoading, setSecurityLoading] = useState(false);

  // Load user data from Firestore
  useEffect(() => {
    if (!currentUser) return;

    setDisplayName(currentUser.displayName || '');
    setPhotoURL(currentUser.photoURL || '');

    const loadProfileData = async () => {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.phoneNumber) setPhoneNumber(data.phoneNumber);
          if (data.address) setAddress(data.address);
          if (data.city) setCity(data.city);
          if (data.postalCode) setPostalCode(data.postalCode);
          if (data.country) setCountry(data.country);
        }

        // Load order stats
        const orders = await getUserOrders(currentUser.uid);
        setUserOrders(orders);
      } catch (err) {
        console.error('Error loading user profile:', err);
      }
    };

    loadProfileData();
  }, [currentUser]);

  // Handle saving personal info
  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    setLoading(true);

    try {
      // 1. Update Firebase Auth Profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: displayName.trim(),
          photoURL: photoURL.trim() || null,
        });
      }

      // 2. Update Firestore document
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(
        userDocRef,
        {
          displayName: displayName.trim(),
          phoneNumber: phoneNumber.trim(),
          photoURL: photoURL.trim() || null,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      // 3. Update localStorage cachedAuthProfile
      try {
        const cached = JSON.parse(localStorage.getItem('cachedAuthProfile') || '{}');
        const updated = {
          ...cached,
          displayName: displayName.trim(),
          photoURL: photoURL.trim() || null,
        };
        localStorage.setItem('cachedAuthProfile', JSON.stringify(updated));
      } catch (e) {}

      toast.success('Atelier Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  // Handle saving default shipping address
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    setLoading(true);

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const addressData = {
        address: address.trim(),
        city: city.trim(),
        postalCode: postalCode.trim(),
        country: country.trim(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(userDocRef, addressData, { merge: true });

      // Sync with checkout form data for instant 1-click checkout
      const currentCheckout = JSON.parse(localStorage.getItem('checkoutFormData') || '{}');
      localStorage.setItem(
        'checkoutFormData',
        JSON.stringify({
          ...currentCheckout,
          name: displayName || currentCheckout.name || '',
          email: currentUser.email,
          phoneNumber: phoneNumber || currentCheckout.phoneNumber || '',
          ...addressData,
        })
      );

      toast.success('Default delivery address saved! Checkout will now pre-fill automatically.');
    } catch (err) {
      console.error('Error saving address:', err);
      toast.error('Failed to save address.');
    } finally {
      setLoading(false);
    }
  };

  // Handle updating password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setSecurityLoading(true);

    try {
      if (currentPassword) {
        const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, cred);
      }

      await updatePassword(auth.currentUser, newPassword);
      toast.success('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error updating password:', err);
      if (err.code === 'auth/wrong-password') {
        toast.error('Incorrect current password.');
      } else if (err.code === 'auth/requires-recent-login') {
        toast.error('Security verification required. Please re-enter your current password.');
      } else {
        toast.error(err.message || 'Failed to update password.');
      }
    } finally {
      setSecurityLoading(false);
    }
  };

  const initialLetter = (displayName || currentUser?.email || 'M').charAt(0).toUpperCase();

  return (
    <Helmet title="My Profile | L'HABILLEUR Atelier">
      <CommonSection title="Client Account & Settings" />

      <section className="profile__section">
        <Container className="profile__container">
          {/* Top Banner Card */}
          <div className="profile__banner-card">
            <div className="profile__header-flex">
              <div className="profile__user-info">
                <div className="profile__avatar-wrapper">
                  {photoURL ? (
                    <img
                      src={photoURL}
                      alt={displayName}
                      className="profile__avatar-img"
                      onError={() => setPhotoURL('')}
                    />
                  ) : (
                    <div className="profile__avatar-initial">{initialLetter}</div>
                  )}
                </div>

                <div className="profile__details">
                  <h3>{displayName || 'Distinguished Member'}</h3>
                  <p>{currentUser?.email}</p>
                  <span className="profile__role-tag">
                    <i className="ri-shield-star-line"></i> {currentUser?.role || 'Customer'}
                  </span>
                </div>
              </div>

              {/* Quick Stats Summary */}
              <div className="profile__quick-stats">
                <div className="profile__stat-box">
                  <div className="profile__stat-num">{userOrders.length}</div>
                  <div className="profile__stat-label">Orders</div>
                </div>
                <div className="profile__stat-box">
                  <div className="profile__stat-num">
                    {userOrders.filter((o) => (o.status || '').toLowerCase() === 'delivered').length}
                  </div>
                  <div className="profile__stat-label">Delivered</div>
                </div>
                <Link to="/orders" className="profile__save-btn" style={{ textDecoration: 'none' }}>
                  <i className="ri-shopping-bag-3-line"></i> View Orders
                </Link>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="profile__tabs-nav">
            <button
              className={`profile__tab-button ${activeTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActiveTab('personal')}
            >
              <i className="ri-user-settings-line"></i> Personal Details
            </button>
            <button
              className={`profile__tab-button ${activeTab === 'address' ? 'active' : ''}`}
              onClick={() => setActiveTab('address')}
            >
              <i className="ri-map-pin-user-line"></i> Address Book
            </button>
            <button
              className={`profile__tab-button ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <i className="ri-lock-password-line"></i> Security & Password
            </button>
          </div>

          {/* Tab 1: Personal Details */}
          {activeTab === 'personal' && (
            <motion.div
              className="profile__content-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="profile__card-title">
                <i className="ri-user-smile-line"></i> Personal Information
              </div>
              <p className="profile__card-subtitle">
                Manage your public atelier name, contact details, and custom avatar.
              </p>

              <form onSubmit={handleSavePersonalInfo}>
                <Row>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>Full Display Name</label>
                      <input
                        type="text"
                        className="profile__input"
                        placeholder="e.g. Mohamed Saleh"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>Registered Email Address</label>
                      <input
                        type="email"
                        className="profile__input"
                        value={currentUser?.email || ''}
                        disabled
                        title="Registered account email"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>Phone Number (for Courier & White-Glove Updates)</label>
                      <input
                        type="tel"
                        className="profile__input"
                        placeholder="+20 100 000 0000"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>Avatar Photo URL (Optional)</label>
                      <input
                        type="url"
                        className="profile__input"
                        placeholder="https://images.unsplash.com/photo-..."
                        value={photoURL}
                        onChange={(e) => setPhotoURL(e.target.value)}
                      />
                    </div>
                  </Col>
                </Row>

                <button type="submit" className="profile__save-btn mt-3" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i> Save Changes
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Tab 2: Saved Address Book */}
          {activeTab === 'address' && (
            <motion.div
              className="profile__content-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="profile__card-title">
                <i className="ri-truck-line"></i> Saved Delivery Address
              </div>
              <p className="profile__card-subtitle">
                Save your standard residence or design office address to enjoy instant pre-filled express checkout.
              </p>

              <form onSubmit={handleSaveAddress}>
                <div className="profile__form-group">
                  <label>Street Address / Apartment / Suite</label>
                  <input
                    type="text"
                    className="profile__input"
                    placeholder="e.g. 15 El-Gezira St, Zamalek, 4th Floor"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>

                <Row>
                  <Col md={4}>
                    <div className="profile__form-group">
                      <label>City / District</label>
                      <input
                        type="text"
                        className="profile__input"
                        placeholder="e.g. Cairo"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="profile__form-group">
                      <label>Postal / Zip Code</label>
                      <input
                        type="text"
                        className="profile__input"
                        placeholder="e.g. 11211"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="profile__form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        className="profile__input"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                </Row>

                <button type="submit" className="profile__save-btn mt-3" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-3-line"></i> Save Delivery Address
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Tab 3: Security & Password */}
          {activeTab === 'security' && (
            <motion.div
              className="profile__content-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="profile__card-title">
                <i className="ri-shield-keyhole-line"></i> Account Security & Credentials
              </div>
              <p className="profile__card-subtitle">
                Ensure your atelier credentials remain private and up to date.
              </p>

              <form onSubmit={handleChangePassword}>
                <div className="profile__form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    className="profile__input"
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>

                <Row>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>New Password (min 6 characters)</label>
                      <input
                        type="password"
                        className="profile__input"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile__form-group">
                      <label>Confirm New Password</label>
                      <input
                        type="password"
                        className="profile__input"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                </Row>

                <button type="submit" className="profile__save-btn mt-3" disabled={securityLoading}>
                  {securityLoading ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i> Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-unlock-line"></i> Update Password
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </Container>
      </section>
    </Helmet>
  );
};

export default Profile;
